import { useForm, type Resolver } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'

import { TextInput } from '@atoms'
import Checkbox from '@atoms/form/Checkbox'
import { FormDialog } from '@components/molecules/FormDialog'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { useShiftAuthoring } from './ShiftAuthoringContext'
import { useEditShiftMutation } from './useEditShiftMutation'
import { shiftFieldsSchema } from './shiftFieldsSchema'
import { type RoleOption } from './options'
import { RoleField } from './RoleField'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('author_shifts')
const tShift = scopedTranslation('attributes.shift')

type FormValues = {
  role: RoleOption | null
  startTime: string
  endTime: string
  volunteersRequired: number
  allowSelfSelect: boolean
}

type EditShiftDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  shift: ShiftWithSlots
}

export function EditShiftDialog({ open, onOpenChange, shift }: EditShiftDialogProps) {
  const { roles, timeZone, onChange } = useShiftAuthoring()
  const { editShift, isPending } = useEditShiftMutation()
  const assignedCount = shift.shiftSlots.assignedCount
  const minVolunteers = Math.max(1, assignedCount)

  const validationSchema = yup.object({
    ...shiftFieldsSchema({
      minVolunteers,
      minVolunteersMessage: assignedCount > 0 ? t('validation.min_assigned', { count: assignedCount }) : undefined,
    }),
    allowSelfSelect: yup.boolean().required(),
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as Resolver<FormValues>,
    mode: 'onTouched',
    defaultValues: {
      role: { value: shift.roleName, label: shift.roleName },
      startTime: timeFormatter.fromISO(shift.startTime, timeZone).to24hours(),
      endTime: timeFormatter.fromISO(shift.endTime, timeZone).to24hours(),
      volunteersRequired: shift.shiftSlots.totalCount,
      allowSelfSelect: shift.allowSelfSelect,
    },
  })

  const onSubmit = handleSubmit((values) => {
    editShift({
      id: shift.id,
      shift: {
        roleName: (values.role?.value ?? '').trim(),
        startTime: timeFormatter.combineDateAndTime(shift.startTime, values.startTime, timeZone),
        endTime: timeFormatter.combineDateAndTime(shift.endTime, values.endTime, timeZone),
        volunteersRequired: values.volunteersRequired,
        draft: shift.draft,
        allowSelfSelect: values.allowSelfSelect,
      },
      onSuccess: () => {
        onOpenChange(false)
        onChange()
      },
    })
  })

  return (
    <FormDialog.Root open={open} onOpenChange={onOpenChange} size="lg" testId="edit-shift-dialog">
      <FormDialog.Title errorsPath="rosterAssignment">{t('edit_title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={onSubmit}>
        <FormDialog.Body>
          <div className="tw:flex tw:gap-3">
            <div className="tw:w-36">
              <TextInput type="time" label={tShift('start_time')} errors={errors} {...register('startTime')} />
            </div>
            <div className="tw:w-36">
              <TextInput type="time" label={tShift('end_time')} errors={errors} {...register('endTime')} />
            </div>
          </div>
          <RoleField control={control} name="role" roles={roles} errors={errors} />
          <TextInput
            type="number"
            min={minVolunteers}
            label={tShift('volunteers')}
            errors={errors}
            description={assignedCount > 0 ? t('min_assigned_hint', { count: assignedCount }) : undefined}
            inputWrapperClassName="tw:w-36 tw:flex-none"
            {...register('volunteersRequired', { valueAsNumber: true })}
          />
          <div className="tw:flex tw:flex-wrap tw:items-center tw:w-full">
            <Checkbox name="allowSelfSelect" control={control} errors={errors}>
              {t('self_select_label')}
            </Checkbox>
          </div>
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('save')} isLoading={isPending} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
